import type { IExecProject } from "../../types/agent-pipelines";

export type StatusFilter =
  | "all"
  | "submitted_to_client"
  | "in_progress"
  | "completed"
  | "not_started";

export type StatusTone = "blue" | "green" | "grey" | "yellow";

export enum RaasPortalId {
  Client = "client",
  Internal = "internal",
}

// Internal Create Project presentation state only. This controls which fields
// are shown, but is never persisted or sent to the exec-project API.
export enum RaasProjectClientMode {
  Enterprise = "enterprise",
  Agency = "agency",
}

export enum RaasProjectViewMode {
  Edit = "edit",
  PreviewAsClient = "preview_as_client",
}

export enum RaasPreviewChatMode {
  ArtifactOnly = "artifact_only",
  Chat = "chat",
}

export enum RaasProjectPreviewSurfaceTrigger {
  ManualPreview = "manual_preview",
  Never = "never",
}

export enum RaasProjectViewActionKey {
  EnterPreviewAsClient = "enter_preview_as_client",
  ExitPreviewAsClient = "exit_preview_as_client",
  SubmitToClient = "submitted_to_client",
}

export enum RaasProjectWorkspaceSurface {
  Preview = "preview",
  Standard = "standard",
}

export enum RaasProjectToolbarPlacement {
  BelowTitle = "below_title",
  Inline = "inline",
}

export type ProjectTableColumnKey =
  | "project"
  | "client"
  | "acting_user_id"
  | "hiring_for"
  | "milestone"
  | "status"
  | "pipeline"
  | "updated_at"
  | "actions";

export type ProjectToolbarControlKey =
  | "search"
  | "status_filter"
  | "org_selector"
  | "impersonation_selector"
  | "client_filter"
  | "assigned_to_filter"
  | "create_project"
  | "upload_template";

export type ProjectFormFieldKey =
  | "project_name"
  | "job_title"
  | "acting_user_id"
  | "client"
  | "hiring_for";

export interface IRaasPortalDefinition {
  id: RaasPortalId;
  basePath: string;
  defaultProjectViewMode: RaasProjectViewMode;
  projectPreviewSurfaceTrigger: RaasProjectPreviewSurfaceTrigger;
  projectViewActionKeys: readonly RaasProjectViewActionKey[];
  projectViewModes: readonly RaasProjectViewMode[];
  tableColumnKeys: readonly ProjectTableColumnKey[];
  toolbarControlKeys: readonly ProjectToolbarControlKey[];
  projectFormFieldKeys: readonly ProjectFormFieldKey[];
}

export interface IProjectTableColumnDefinition {
  key: ProjectTableColumnKey;
  label: string;
  width: number;
}

export interface IRaasProjectViewState {
  actionKeys: readonly RaasProjectViewActionKey[];
  mode: RaasProjectViewMode;
  surface: RaasProjectWorkspaceSurface;
}

export interface IRaasProjectViewController extends IRaasProjectViewState {
  onAction: (actionKey: RaasProjectViewActionKey) => void;
}

export interface IProjectFormData {
  project_name: string;
  job_title: string;
  acting_user_id: string;
  client_name: string;
  client_company_id: string;
  client_company_account_id: string;
  client_domain: string;
  hiring_company_name: string;
  hiring_company_id: string;
}

export interface IStatusView {
  status: Exclude<StatusFilter, "all">;
  label: string;
  tone: StatusTone;
  muted?: boolean;
}

export interface IRaasProjectUser {
  email?: string;
  id: string;
  name: string;
}

export interface IProjectRow {
  project: IExecProject;
  projectNameLabel: string;
  actingUser?: IRaasProjectUser;
  actingUserEmailLabel?: string;
  actingUserLabel: string;
  clientFilterValue?: string;
  clientLabel: string;
  clientCompanyMetaRef?: string;
  hiringForLabel: string;
  hiringForCompanyMetaRef?: string;
  milestoneLabel: string;
  isPipelineLoading?: boolean;
  pipelineLabel: string;
  status: IStatusView;
  updatedAtLabel: string;
  updatedAtTitle: string;
}

export interface IFilterOption {
  label: string;
  value: string;
}

export type SortOrder = "asc" | "desc";

export type SortableColumnKey = "project" | "status" | "updated_at";

export interface ISortConfig {
  field: SortableColumnKey;
  order: SortOrder;
}

export const SORTABLE_COLUMN_API_FIELD: Record<SortableColumnKey, string> = {
  project: "project_name",
  status: "status",
  updated_at: "updated_at",
};
